<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import CreateCustomToolDialog from 'dashboard/components-next/assistant/pageComponents/customTool/CreateCustomToolDialog.vue';
import CustomToolCard from 'dashboard/components-next/assistant/shared/CustomToolCard.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';
import McpServersSection from 'dashboard/components-next/assistant/pageComponents/mcp/McpServersSection.vue';

const store = useStore();

const uiFlags = useMapGetter('assistantCustomTools/getUIFlags');
const customTools = useMapGetter('assistantCustomTools/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const customToolsMeta = useMapGetter('assistantCustomTools/getMeta');

// Поиск по загруженным записям (клиентская фильтрация — под масштаб SMB).
const searchQuery = ref('');
const filteredTools = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return customTools.value;
  return picoSearch(customTools.value, query, ['title', 'description']);
});

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedTool = ref(null);
const dialogType = ref('');

const fetchCustomTools = (page = 1) => {
  store.dispatch('assistantCustomTools/get', { page });
};

const onPageChange = page => fetchCustomTools(page);

const openCreateDialog = () => {
  dialogType.value = 'create';
  selectedTool.value = null;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleEdit = tool => {
  dialogType.value = 'edit';
  selectedTool.value = tool;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleDelete = tool => {
  selectedTool.value = tool;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  const tool = customTools.value.find(t => t.id === id);
  if (action === 'edit') {
    handleEdit(tool);
  } else if (action === 'delete') {
    handleDelete(tool);
  }
};

const handleDialogClose = () => {
  dialogType.value = '';
  selectedTool.value = null;
};

const onDeleteSuccess = () => {
  selectedTool.value = null;
  // Check if page will be empty after deletion
  if (customTools.value.length === 1 && customToolsMeta.value.page > 1) {
    onPageChange(customToolsMeta.value.page - 1);
  } else {
    fetchCustomTools(customToolsMeta.value.page);
  }
};

onMounted(() => fetchCustomTools());
</script>

<template>
  <PageLayout
    v-model:search-query="searchQuery"
    :header-title="$t('ASSISTANT.TOOLS.HEADER')"
    :description="$t('ASSISTANT.TOOLS.PAGE_DESCRIPTION')"
    :search-placeholder="$t('ASSISTANT.TOOLS.SEARCH_PLACEHOLDER')"
    :button-label="$t('ASSISTANT.TOOLS.ADD_NEW')"
    :button-policy="['administrator']"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    :total-count="customToolsMeta.totalCount"
    :current-page="customToolsMeta.page"
    :show-pagination-footer="!isFetching && !!customTools.length"
    :is-fetching="isFetching"
    :is-empty="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #body>
      <div class="flex flex-col gap-4">
        <!-- Пустой стейт HTTP-инструментов инлайном: ниже живёт секция
             MCP-серверов, поэтому полноэкранный empty state не используется. -->
        <p
          v-if="!customTools.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('ASSISTANT.AGENTS.FORM.TOOLS.EMPTY') }}
        </p>
        <p
          v-else-if="!filteredTools.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('ASSISTANT.TOOLS.SEARCH_EMPTY') }}
        </p>
        <CustomToolCard
          v-for="tool in filteredTools"
          :id="tool.id"
          :key="tool.id"
          :title="tool.title"
          :description="tool.description"
          :endpoint-url="tool.endpoint_url"
          :http-method="tool.http_method"
          :auth-type="tool.auth_type"
          :param-schema="tool.param_schema"
          :enabled="tool.enabled"
          :created-at="tool.created_at"
          :updated-at="tool.updated_at"
          @action="handleAction"
        />

        <!-- MCP-серверы (Р9.4) -->
        <McpServersSection />
      </div>
    </template>
  </PageLayout>

  <CreateCustomToolDialog
    v-if="dialogType"
    ref="createDialogRef"
    :type="dialogType"
    :selected-tool="selectedTool"
    @close="handleDialogClose"
  />

  <DeleteDialog
    v-if="selectedTool"
    ref="deleteDialogRef"
    :entity="selectedTool"
    type="CustomTools"
    translation-key="TOOLS"
    @delete-success="onDeleteSuccess"
  />
</template>
